import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import cogoToast from "cogo-toast";

// Context
import { Consumer } from "../../context";

// Middlewares
import Permissions from "../../middlewares/Permissions";

// Components
import Header from "../../components/header/header";
import SidebarAdministracion from "../../components/header/sidebarAdministracion";
import Controls from "../../components/controls/controls";
import ViviendasPagosTable from "./components/ViviendasPagosTable";
import SuperCore from "../../components/core/SuperCore";
import SuperModal2 from "../../components/coreModal/SuperModal2";
import Importar from "../../components/Billing/Importar";
import Exportar from "../../components/Billing/Exportar";
import Eliminar from "../../components/Billing/Eliminar";
import NuevoDocumento from "./components/CreateDoc";
import GeneracionDocumentos from "./components/generalDoc";

// Requests
import { getSubfraccionamientos, getViviendas, getMetodosPago } from "../../helpers/requests";
import {
    getViviendasDocuments,
    getViviendaDataDocuments,
    postCreateGeneracion,
    postImportDocument,
    postCreateImportDocuments,
    getExportarDocuments,
    getConfigDocuments,
    postEliminarDocuments,
    postCreateDocuments,
} from "./helpers/requests";

const superCore = new SuperCore();

const cogoToastOptionsRight = { position: "bottom-right", hideAfter: 5 };
const cogoToastOptionsCenter = { position: "bottom-center", hideAfter: 5 };

const idGeneracionModal = "newDoc";
const idImportarModal = "importar";
const idExportarModal = "exportar";
const idEliminarModal = "eliminar";
const idCreateDocModal = "createDocument";

function CargosYPagosPage(props) {
    const { context, location } = props;
    const { getSubmenuAdminName, submenuAdminName, fraccionamiento } = context;

    const id_frac = useRef(+(localStorage.getItem("frac") || 0));

    const [subfraccionamientos, setSubfraccionamientos] = useState([]);
    const [viviendas, setViviendas] = useState([]);
    const [metodosPago, setMetodosPago] = useState([]);
    const [permissions, setPermissions] = useState({ lectura: false, escritura: false });
    const [user, setUser] = useState({ name: "", id: 0 });

    const [documentos, setDocumentos] = useState([]);
    const [loadingDocumentos, setLoadingDocumentos] = useState(true);

    const [openModalId, setOpenModalId] = useState({});
    const [textFilter, setTextFilter] = useState("");

    const loadUser = useCallback(async () => {
        const user = await superCore.getUserInfo();
        setUser({ name: user.info.usuario, id: user.info.id_usuario });
    }, []);

    const loadPermissions = useCallback(async () => {
        const permisos = new Permissions();

        const { escritura, lectura } = await permisos.getResult();
        setPermissions({ escritura, lectura });
    }, []);

    const loadDocumentos = useCallback(async id_frac => {
        setLoadingDocumentos(true);

        const { documentos } = await getViviendasDocuments(id_frac);

        setLoadingDocumentos(false);

        setDocumentos(documentos);
    }, []);

    const loadSubfraccionamientos = useCallback(async id_frac => {
        const { subfraccionamientos } = await getSubfraccionamientos(id_frac);
        setSubfraccionamientos(subfraccionamientos);
    }, []);

    const loadViviendas = useCallback(async id_frac => {
        const { viviendas } = await getViviendas(id_frac);

        setViviendas(viviendas);
    }, []);

    const loadMetodosPago = useCallback(async id_frac => {
        const { metodosPago } = await getMetodosPago();
        setMetodosPago(metodosPago);
    }, []);

    useEffect(() => {
        getSubmenuAdminName(location.pathname);
    }, [getSubmenuAdminName, location]);

    useEffect(() => {
        const idFrac = id_frac.current;

        loadUser();
        loadPermissions();
        loadSubfraccionamientos(idFrac);
        loadViviendas(idFrac);
        loadMetodosPago();
        loadDocumentos(idFrac);
    }, [loadUser, loadPermissions, loadSubfraccionamientos, loadViviendas, loadMetodosPago, loadDocumentos]);

    const onCloseModal = () => setOpenModalId("");

    const documentosEnriquecidos = documentos.map(doc => {
        const viv = viviendas.find(v => v.id_vivienda === doc.Id_Vivienda);
        return {
            ...doc,
            id_subfraccionamiento: viv?.id_subfraccionamiento,
            nombre_subfrac: viv?.nombre_subfrac
        };
    });
    const normalizedFilter = textFilter.toLowerCase().trim();

    const filteredDocumentos = documentosEnriquecidos.filter(
        doc =>
            (doc.Unidad || "").toLowerCase().includes(normalizedFilter) ||
            (doc.Propietario || "").toLowerCase().includes(normalizedFilter),
    );

    return (
        <div className="column" /*admin*/>
            <Helmet>
                <title>
                    {process.env.REACT_APP_NAME} - {submenuAdminName}
                </title>
            </Helmet>
            <Header
                id="header"
                reload={() => console.log("Add reload")}
                change={() => console.log("Add change")}
                sidebar={true}
                nav={"administracion"}
            />
            <div className="row full">
                <div className="column" id="sidebar">
                    <SidebarAdministracion />
                </div>
                <div id="content" className="column align-center">
                    <div className="container column" style={{ flex: 1 }}>
                        <Controls
                            id="controls"
                            title={submenuAdminName}
                            nuevo={() => setOpenModalId(idCreateDocModal)}
                            dataSet={documentos}
                            newButton={permissions.escritura}
                            setFilter={value => setTextFilter(value)}
                            selectSub={false}
                        />
                        <ButtonControls onOpenModals={idModal => setOpenModalId(idModal)} />
                        <ViviendasPagosTable
                            id={"docs_table"}
                            docs={filteredDocumentos}
                            loadingDocs={loadingDocumentos}
                            subfraccionamientos={subfraccionamientos}
                            escritura={permissions.escritura}
                            fracName={fraccionamiento}
                            userName={user.name}
                            userId={user.id}
                            metodosPago={metodosPago}
                            onReload={() => loadDocumentos(id_frac.current)}
                        />
                    </div>
                </div>
            </div>
            <GeneracionDocumentosModal
                id={idGeneracionModal}
                visible={openModalId === idGeneracionModal}
                user={user}
                onCloseModal={onCloseModal}
                onSuccessfulSubmit={() => {
                    loadDocumentos(id_frac.current);
                    onCloseModal();
                }}
            />
            <ImportarDocumentosModal
                id={idImportarModal}
                visible={openModalId === idImportarModal}
                onCloseModal={onCloseModal}
                onSuccessfulSubmit={() => {
                    loadDocumentos(id_frac.current);
                    onCloseModal();
                }}
            />
            <ExportarDocumentosModal
                id={idExportarModal}
                visible={openModalId === idExportarModal}
                frac_name={fraccionamiento}
                onCloseModal={onCloseModal}
                onSuccessfulSubmit={onCloseModal}
            />
            <EliminarModal
                id={idEliminarModal}
                visible={openModalId === idEliminarModal}
                viviendas={viviendas}
                getUserInfo={superCore.getUserInfo}
                onCloseModal={onCloseModal}
                onSuccessfulSubmit={() => {
                    loadDocumentos(id_frac.current);
                    onCloseModal();
                }}
            />
            <NuevoDocumentoModal
                id={idCreateDocModal}
                visible={openModalId === idCreateDocModal}
                user={user}
                viviendas={viviendas}
                onCloseModal={onCloseModal}
                onSuccessfulSubmit={() => {
                    loadDocumentos(id_frac.current);
                    onCloseModal();
                }}
            />
        </div>
    );
}

export default Consumer(CargosYPagosPage);

const ButtonControls = props => {
    const { onOpenModals } = props;

    return (
        <>
            <style>{`
                      #second_controls {
                        position: sticky;
                        top: 216px;
                        z-index: 3;
                        background-color: white;
                      }
                    `}</style>
            <div className="row" id="second_controls">
                <div className="row-responsive full justify-start">
                    <div className="data-to column justify-center" style={{ maxWidth: "130px" }}>
                        <button
                            className="btn btn-primary btn-small color-white"
                            onClick={() => onOpenModals(idGeneracionModal)}
                        >
                            <i className="fas fa-file"></i> &nbsp;Generar
                        </button>
                    </div>
                    &nbsp;
                    <div className="data-to column justify-center" style={{ maxWidth: "130px" }}>
                        <button
                            className="btn btn-primary btn-small color-white"
                            onClick={() => onOpenModals(idImportarModal)}
                        >
                            <i className="fas fa-file-upload"></i> &nbsp; Importar
                        </button>
                    </div>
                    &nbsp;
                    <div className="data-to column justify-center" style={{ maxWidth: "130px" }}>
                        <button
                            className="btn btn-primary btn-small color-white"
                            onClick={() => onOpenModals(idExportarModal)}
                        >
                            <i className="fas fa-file-excel"></i> &nbsp; Exportar
                        </button>
                    </div>
                    &nbsp;
                    <div className="data-to column justify-center" style={{ maxWidth: "130px" }}>
                        <button
                            className="btn btn-primary btn-small color-white"
                            onClick={() => onOpenModals(idEliminarModal)}
                        >
                            <i className="fas fa-trash"></i> &nbsp; Eliminar
                        </button>
                    </div>
                </div>
            </div>
            <div className="white-space-16"></div>
        </>
    );
};

const GeneracionDocumentosModal = props => {
    const { id, visible, user, onCloseModal, onSuccessfulSubmit } = props;

    const [generacionData, setGeneracionData] = useState({
        dataDocs: [],
        confirmDocs: false,
        loadingGet: false,
        loadingCreate: false,
    });

    const { dataDocs, confirmDocs, loadingGet, loadingCreate } = generacionData;

    const totalDocs = dataDocs?.reduce((a, b) => a + b.no_docs, 0) || 0;

    const [date0, date1] = dataDocs.length > 0 ? Object.values(dataDocs[0])[1].split("/") : [null, null];
    const disableConfirmDate = dataDocs.length > 0 ? new Date(date1, date0 - 1, 1) : undefined;
    dataDocs.length > 0 && disableConfirmDate.setDate(disableConfirmDate.getDate() - 3);
    const disableConfirm = dataDocs.length > 0 ? disableConfirmDate > new Date() : false;

    const monthStart = dataDocs.length > 0 ? new Date(date1, date0 - 1) : new Date();

    const frac_name = "";
    const subfrac_name = "";
    const userName = "";
    const subfraccionamientos = [];
    const generation = "";

    const onUpdateData = useCallback(data => setGeneracionData(prevData => ({ ...prevData, ...data })), []);

    const loadGeneracionDocumentos = useCallback(
        async id_frac => {
            onUpdateData({ loadingGet: true });

            const { dataDocs } = await getViviendaDataDocuments(id_frac);

            onUpdateData({
                dataDocs: dataDocs || [],
                confirmDocs: false,
                loadingGet: false,
                loadingCreate: false
            });
        },
        [onUpdateData],
    );

    const loadCreateGeneracion = async event => {
        const validateCreateGeneracion = async () => {
            const monthNum = monthStart.getMonth();
            const fullYear = monthStart.getFullYear();

            if (totalDocs === 0) return { invalid: true, message: "No existen documentos para crear." };

            const data = {
                id_fraccionamiento: +(localStorage.getItem("frac") || 0),
                id_subfraccionamiento: +(localStorage.getItem("subfrac") || 0),
                mes: monthNum + 1,
                year: fullYear,
                user: user.id,
            };

            const response = await postCreateGeneracion(data);

            return response;
        };

        onUpdateData({ loadingCreate: true });

        const { created, invalid, message } = await validateCreateGeneracion();

        onUpdateData({ loadingCreate: false });

        if (created) {
            cogoToast.success(message, cogoToastOptionsCenter);
            onUpdateData({ dataDocs: [], confirmDocs: false });
            onSuccessfulSubmit && onSuccessfulSubmit();
        } else if (invalid) cogoToast.warn(message, cogoToastOptionsCenter);
        else cogoToast.error(message, cogoToastOptionsCenter);
    };

    useEffect(() => {
        if (visible) {
            const id_frac = +(localStorage.getItem("frac") || 0);
            loadGeneracionDocumentos(id_frac);
        }
    }, [visible, loadGeneracionDocumentos]);

    const screen = [
        {
            component: (
                <GeneracionDocumentos
                    updateData={onUpdateData}
                    monthStart={monthStart}
                    generation={generation}
                    fracName={frac_name}
                    subfracs={subfraccionamientos}
                    subfrac={subfrac_name}
                    userName={userName}
                    dataDocs={dataDocs}
                    totalDocs={totalDocs}
                    confirmDocs={confirmDocs}
                    loadingDataGeneracion={loadingGet}
                    disabledConfirm={disableConfirm}
                    disableConfirmDate={disableConfirmDate}
                />
            ),
        },
    ];

    const cancelProps = SuperModal2.SetFooterCancelProps({ label: "CANCELAR" });

    const submitProps = SuperModal2.SetFooterSubmitProps({
        show: confirmDocs,
        label: "GENERAR",
        actionCheck: loadCreateGeneracion,
    });

    return (
        <SuperModal2
            id={id}
            title={"Generación de documentos de cobro"}
            size={"small"}
            menuScreens={screen}
            openModal={visible}
            loading={loadingCreate}
            cancelProps={cancelProps}
            submitProps={submitProps}
            onCloseModal={onCloseModal}
        />
    );
};

const ImportarDocumentosModal = props => {
    const { id, visible, onCloseModal, onSuccessfulSubmit } = props;

    const importarRef = useRef(null);
    const [loading, setLoading] = useState(false);

    const id_fraccionamiento = useMemo(() => +(localStorage.getItem("frac") || 0), []);

    const visibleRef = useRef(visible);

    useEffect(() => {
        if (visibleRef.current && !visible && importarRef.current?.resetForm) {
            importarRef.current.resetForm();
        }
        visibleRef.current = visible;
    }, [visible]);

    const loadImportDocument = async () => {
        if (!importarRef.current) return false;

        const summary = importarRef.current.collectBulkPayload();

        if (!summary) {
            cogoToast.error("No se pudo leer la información del archivo.", cogoToastOptionsCenter);
            return false;
        }

        if (Array.isArray(summary.errors) && summary.errors.length > 0) {
            cogoToast.warn("Corrige los errores antes de importar.", cogoToastOptionsCenter);
            return false;
        }

        if (!Array.isArray(summary.rows) || summary.rows.length === 0) {
            cogoToast.warn("No hay registros válidos para importar.", cogoToastOptionsCenter);
            return false;
        }

        setLoading(true);
        cogoToast.info("Importando...", cogoToastOptionsCenter);

        const user = await superCore.getUserInfo();

        const result = await postCreateImportDocuments({
            rows: summary.rows,
            usuario: user.info.usuario,
        });

        setLoading(false);

        if (result.created) {
            const count = result.count || summary.rows.length;
            cogoToast.success(
                `${count} documento${count > 1 ? "s" : ""} importado${count > 1 ? "s" : ""} correctamente.`,
                cogoToastOptionsCenter,
            );
            onSuccessfulSubmit && onSuccessfulSubmit();
        } else {
            cogoToast.error(
                result.message || "Error al importar los documentos.",
                cogoToastOptionsCenter,
            );
        }
    };

    const screen = [
        {
            component: (
                <Importar
                    ref={importarRef}
                    id_fraccionamiento={id_fraccionamiento}
                />
            ),
        },
    ];

    const submitProps = SuperModal2.SetFooterSubmitProps({
        show: true,
        label: "ACEPTAR",
        actionCheck: loadImportDocument,
    });

    const cancelProps = SuperModal2.SetFooterCancelProps({
        show: true,
        label: "CERRAR",
    });

    return (
        <SuperModal2
            id={id}
            title={"Importación de documentos de cobro"}
            size={"huge"}
            menuScreens={screen}
            openModal={visible}
            loading={loading}
            submitProps={submitProps}
            cancelProps={cancelProps}
            onCloseModal={onCloseModal}
        />
    );
};

const ExportarDocumentosModal = props => {
    const { id, visible, frac_name, onCloseModal, onSuccessfulSubmit } = props;

    const [exportarData, setExportarData] = useState({
        startDate: new Date(),
        dataExport: [],
        loading: false,
    });

    const onUpdateData = data => setExportarData(prevData => ({ ...prevData, ...data }));

    const loadExportarData = async () => {
        onUpdateData({ loading: true });

        const toastLoading = cogoToast.loading("Buscando datos que exportar.", { position: "bottom-center" });

        const mes = exportarData.startDate.getMonth() + 1;

        const data = {
            id_fraccionamiento: +(localStorage.getItem("frac") || 0),
            date: `${mes < 10 ? "0" + mes : mes}${exportarData.startDate.getFullYear()}`,
        };

        const { dataExport, error, message } = await getExportarDocuments(data);

        onUpdateData({ loading: false });

        toastLoading.then(() => {
            if (dataExport) {
                onUpdateData({ dataExport });
                let btn_export = document.getElementById("btn-export");
                if (btn_export) {
                    btn_export.click();
                    cogoToast.success(message, { position: "bottom-center", hideAfter: 30 });

                    onSuccessfulSubmit && onSuccessfulSubmit();
                }
            } else if (error) cogoToast.error(message, cogoToastOptionsRight);
            else cogoToast.warn(message, cogoToastOptionsRight);
        });
    };

    const screen = [
        {
            component: (
                <Exportar
                    updateData={onUpdateData}
                    startDate={exportarData.startDate}
                    dataSet={exportarData.dataExport}
                    dataExport={exportarData.dataExport}
                    frac={frac_name}
                />
            ),
        },
    ];

    const bodyStyle = { overflowY: "none" };

    const cancelProps = SuperModal2.SetFooterCancelProps({ label: "CANCELAR" });

    const submitProps = SuperModal2.SetFooterSubmitProps({
        show: true,
        label: "ACEPTAR",
        actionCheck: loadExportarData,
    });

    return (
        <SuperModal2
            id={id}
            title={"Exportar documentos de cobro"}
            size={"small"}
            menuScreens={screen}
            openModal={visible}
            bodyStyle={bodyStyle}
            cancelProps={cancelProps}
            submitProps={submitProps}
            onCloseModal={onCloseModal}
        />
    );
};

const EliminarModal = props => {
    const { id, visible, viviendas, getUserInfo, onCloseModal, onSuccessfulSubmit } = props;

    const [eliminarData, setEliminarData] = useState({ cargos: [], confirm: false, loading: false });
    const { cargos, confirm, loading } = eliminarData;

    const [resetToken, setResetToken] = useState(0);

    // Insert hooks to detect close and reset state
    const visibleRef = useRef(visible);

    useEffect(() => {
        // if it was open and now is closed, reset internal state and signal children
        if (visibleRef.current && !visible) {
            setEliminarData({ cargos: [], confirm: false, loading: false });
            setResetToken(t => t + 1);
        }
        visibleRef.current = visible;
    }, [visible]);

    const onUpdateData = data =>
        setEliminarData(prevData => ({
            ...prevData,
            ...data,
        }));

    const screen = [
        {
            component: (
                <Eliminar
                    key={resetToken}
                    confirmEliminar={confirm}
                    cargosEliminar={cargos}
                    viviendas={viviendas}
                    cargos={cargos}
                    onCargosChanged={cargos => onUpdateData({ cargos })}
                    onConfirmEliminar={confirm => onUpdateData({ confirm })}
                    resetToken={resetToken}
                />
            ),
        },
    ];

    const submitEliminar = async event => {
        onUpdateData({ loading: true });

        let user = await getUserInfo();

        const data = {
            array: cargos,
            userId: user.info.id_usuario,
        };

        const { deleted, message } = await postEliminarDocuments(data);

        onUpdateData({ loading: false });

        if (deleted) {
            cogoToast.success(message, cogoToastOptionsCenter);

            onUpdateData({ cargos: [], confirm: false });

            onSuccessfulSubmit && onSuccessfulSubmit();
        } else cogoToast.error(message, cogoToastOptionsCenter);
    };

    const cancelProps = SuperModal2.SetFooterCancelProps({ show: true, label: "CERRAR" });
    const submitProps = SuperModal2.SetFooterSubmitProps({
        show: confirm,
        label: "ACEPTAR",
        actionCheck: submitEliminar,
    });

    return (
        <SuperModal2
            id={id}
            title={"Eliminación de documentos de cobro"}
            openModal={visible}
            menuScreens={screen}
            loading={loading}
            cancelProps={cancelProps}
            submitProps={submitProps}
            onCloseModal={onCloseModal}
        />
    );
};

const lineasNewDocInit = () =>
    Array.from({ length: 6 }, () => ({
        concept: "",
        month: new Date(),
        monto: 0,
        idLinea: Math.ceil(Math.random() * 100) * Math.ceil(Math.random() * 100),
        status: 0,
    }));

const newDocDataInit = () => ({
    lineasNewDoc: lineasNewDocInit(),
    impuestosNewDoc: [],
    idViviendaNew: 0,
    fechaNew: new Date(),
    comentarioNew: "",
    nombre_vivienda: "",
    loading: false,
});

const NuevoDocumentoModal = props => {
    const { id, visible, user, viviendas, onCloseModal, onSuccessfulSubmit } = props;

    const [newDocData, setNewDocData] = useState(newDocDataInit);
    const [config, setConfig] = useState({ Productos: [], Impuestos: [] });

    const visibleRef = useRef(visible);

    const { lineasNewDoc, impuestosNewDoc, idViviendaNew, fechaNew, comentarioNew, nombre_vivienda, loading } =
        newDocData;

    const fracName = "";
    const subfrac = "";
    const userName = "";

    if (visible !== visibleRef.current) {
        visibleRef.current = visible;
        if (visible) setNewDocData(newDocDataInit());
    }

    const onUpdateData = data => setNewDocData(prevData => ({ ...prevData, ...data }));

    const loadConfigDocument = useCallback(async () => {
        const id_fraccionamiento = +(localStorage.getItem("frac") || 0);

        const { configProductos, configImpuestos, error, message } = await getConfigDocuments(id_fraccionamiento);

        if (!error && !message) {
            setConfig({
                Productos: configProductos,
                Impuestos: configImpuestos,
            });
        }
    }, []);

    const loadCreateDocuments = async () => {
        const validateCreateDocuments = async () => {
            if (idViviendaNew === 0)
                return {
                    invalid: true,
                    message: "Debes seleccionar una vivienda.",
                };

            const cuenta = lineasNewDoc.filter(linea => linea.status !== 0);

            if (cuenta.length === 0)
                return {
                    invalid: true,
                    message: "El documento debe tener al menos una línea.",
                };

            const productosLineas = cuenta.filter(pl => pl.concept == "" || pl.concept == 0);

            if (productosLineas.length !== 0)
                return {
                    invalid: true,
                    message: "Existe una línea incompleta.",
                };

            const montoLineas = lineasNewDoc.filter(ln => ln.status === 1 && ln.monto === 0);

            if (montoLineas.length !== 0)
                return {
                    invalid: true,
                    message: "Existe una línea incompleta.",
                };

            const lineas = lineasNewDoc
                .filter(pl => pl.concept !== "" || (pl.concept !== 0 && pl.status === 1))
                .map(lin => {
                    const newLin = { ...lin };
                    newLin.month = (newLin.month.getMonth() + 1 + "").padStart(2, "0") + newLin.month.getFullYear();
                    return newLin;
                });

            const impuestos = impuestosNewDoc.filter(impuesto => impuesto.status !== 0);

            const data = {
                id_vivienda: idViviendaNew,
                fecha: fechaNew,
                comentario: comentarioNew,
                lineas: JSON.stringify(lineas),
                impuestos: JSON.stringify(impuestos),
                userId: user.id,
            };

            const response = await postCreateDocuments(data);

            return response;
        };

        onUpdateData({ loading: true });

        const { created, invalid, message } = await validateCreateDocuments();

        onUpdateData({ loading: false });

        if (created) {
            cogoToast.success(message, cogoToastOptionsRight);
            onSuccessfulSubmit && onSuccessfulSubmit();
        } else if (invalid) cogoToast.warn(message, cogoToastOptionsRight);
        else cogoToast.error(message, cogoToastOptionsRight);
    };

    useEffect(() => {
        loadConfigDocument();
    }, [loadConfigDocument]);

    const screen = [
        {
            component: (
                <NuevoDocumento
                    updateDataNew={onUpdateData}
                    fracName={fracName}
                    subfrac={subfrac}
                    userName={userName}
                    viviendas={viviendas}
                    configProductos={config.Productos}
                    configImpuestos={config.Impuestos}
                    lineasNewDoc={lineasNewDoc}
                    impuestosNewDoc={impuestosNewDoc}
                    idViviendaNew={idViviendaNew}
                    fechaNew={fechaNew}
                    comentarioNew={comentarioNew}
                    nombre_vivienda={nombre_vivienda}
                />
            ),
        },
    ];

    const submitProps = SuperModal2.SetFooterSubmitProps({
        show: true,
        label: "Guardar",
        actionCheck: loadCreateDocuments,
    });

    return (
        <SuperModal2
            id={id}
            size={"big"}
            title={"Nuevo documento de cobro"}
            menuScreens={screen}
            openModal={visible}
            loading={loading}
            submitProps={submitProps}
            onCloseModal={onCloseModal}
        />
    );
};
